<script setup>
import {
  HrIconActionBtn, HrItemLabel, HrListSectionHeader, HrLoadingSpinner, HrMenuItem, HrTooltip,
} from 'src/design-system';
import { getAddProgramMenuTypes } from 'src/utils/ProgramUtils';
import { computed } from 'vue';

defineOptions({ name: 'AddProgramMenuSections' });

const ROW_E2E = {
  client: {
    simple: 'list-item-template-program-skill-acquisition',
    frequency: 'list-item-template-program-frequency',
    interval: 'list-item-template-program-interval',
    duration: 'list-item-template-program-duration',
    taskAnalysis: 'list-item-template-program-task-analysis',
    behaviorTracking: 'list-item-template-client-program-behavior-tracking',
  },
  staff: {
    simple: 'list-item-template-program-skill-acquisition',
    interval: 'list-item-template-program-interval',
    instructions: 'list-item-template-program-instructions',
    asr: 'list-item-template-program-active-student-response',
  },
};

const props = defineProps({
  clientType: {
    type: String,
    required: true,
    validator: (value) => ['client', 'staff'].includes(value),
  },

  templates: {
    type: Array,
    default: () => [],
  },

  isLoadingTemplates: {
    type: Boolean,
    default: false,
  },

  hideTemplates: {
    type: Boolean,
    default: false,
  },

  includeCatalogOnlyProgramTypes: {
    type: Boolean,
    default: false,
  },

  isAdmin: {
    type: Boolean,
    default: false,
  },

  canManageTemplates: {
    type: Boolean,
    default: false,
  },

  disableBehaviorProgram: {
    type: Boolean,
    default: false,
  },

  behaviorTrackingEnabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  'add-program': null,
  'use-program': null,
});

const showTemplates = computed(() => !props.hideTemplates
  && (props.isLoadingTemplates || props.templates.length > 0));

const programTypes = computed(() => getAddProgramMenuTypes({
  clientType: props.clientType,
  includeCatalogOnly: props.includeCatalogOnlyProgramTypes,
  isAdmin: props.isAdmin,
  behaviorTrackingEnabled: props.behaviorTrackingEnabled,
}));
</script>

<template>
  <template v-if="showTemplates">
    <hr-list-section-header class="hr-primary add-program-menu-sections__header">
      <div>
        <div class="text-weight-bold q-mb-xs">
          {{ $t('programs.headingStartFromTemplate') }}
        </div>
        <hr-item-label caption>
          {{ $t('programs.textStartFromTemplate') }}
        </hr-item-label>
      </div>
      <template
        v-if="canManageTemplates"
        #trailing
      >
        <hr-icon-action-btn
          icon="tune"
          size="md"
          :to="{ name: 'Settings - Program Templates' }"
          :aria-label="$t('programs.tooltipManageTemplates')"
          :legacy-props="{ color: 'white' }"
          data-e2e="button-manage-program-templates"
        >
          <hr-tooltip>{{ $t('programs.tooltipManageTemplates') }}</hr-tooltip>
        </hr-icon-action-btn>
      </template>
    </hr-list-section-header>
    <div
      v-if="isLoadingTemplates"
      class="row justify-center q-pa-sm"
    >
      <hr-loading-spinner />
    </div>
    <template v-else>
      <hr-menu-item
        v-for="template in templates"
        :key="template.id"
        v-close-popup
        :label="template.programName"
        @click="emit('use-program', template.id, true)"
      />
    </template>
  </template>

  <hr-list-section-header class="hr-primary add-program-menu-sections__header">
    <div>
      <div class="text-weight-bold q-mb-xs">
        {{ $t('programs.headingStartFromScratch') }}
      </div>
      <hr-item-label caption>
        {{ $t('programs.textStartFromScratch') }}
      </hr-item-label>
    </div>
  </hr-list-section-header>
  <hr-menu-item
    v-for="programType in programTypes"
    :key="programType.type"
    v-close-popup
    :label="$t(programType.labelKey)"
    :disable="programType.type === 'behaviour' && disableBehaviorProgram"
    :data-e2e="ROW_E2E[clientType][programType.type]"
    @click="emit('add-program', programType.type, clientType)"
  />
</template>

<style lang="scss" scoped>
.add-program-menu-sections__header {
  --hr-on-container: var(--app-on-primary);
  color: var(--app-on-primary);

  &.q-item__label.q-item__label--header {
    padding-block: 8px;
  }

  .q-item__label--caption {
    color: var(--app-on-primary);
  }
}
</style>
